import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSofteamboardStore, useActiveNeeds } from '../../store/softeamboardStore';
import { candidatesApi, needsApi } from '../../services/api';
import { isFreshNeed } from '../../utils/needUtils';
import { 
  Plus, Edit, User, Building, 
  AlertCircle, Clock, Loader, Brain, ChevronLeft, ChevronRight, Download, Sparkles
} from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Badge from '../ui/Badge';
import Toast from '../ui/Toast';
import EditNeedModal from './EditNeedModal';
import AppHeader from '../common/AppHeader';
import NeedCreateModal from './NeedCreateModal';
import ProcessTracker from './ProcessTracker';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Optimized NeedRow component - receives candidates data from parent
const NeedRow = ({ need, consultants, columns, onEditClick, onRowClick, getStatusColor, getStatusLabel, getSalesNames, candidatesData, candidatesLoading, candidatesError }) => {
  // Get progress percentage - use progressPct field from API
  const progressPct = need.progressPct || 0;

  const renderCandidatesCell = () => {
    if (candidatesLoading) {
      return (
        <div className="flex items-center">
          <Loader className="animate-spin mr-2" size={12} />
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
      );
    }

    if (candidatesError) {
      return (
        <span className="text-xs text-red-500" title={`Error: ${candidatesError}`}>
          Error loading
        </span>
      );
    }

    const candidateList = candidatesData[need.id] || [];
    
    if (candidateList.length === 0) {
      return <span className="text-gray-500">–</span>;
    }

    const candidateNames = candidateList.map(c => c.name).join(', ');
    
    // Truncate if too long
    if (candidateNames.length > 50) {
      return (
        <span title={candidateNames} className="text-sm text-gray-900">
          {candidateNames.substring(0, 47)}...
        </span>
      );
    }

    return <span className="text-sm text-gray-900">{candidateNames}</span>;
  };

  // Enhanced client display with order clients
  const renderClientCell = () => {
    const hasOrderClients = need.orderClients && need.orderClients.length > 0;
    
    return (
      <div className="flex items-center">
        <Building className="mr-2 text-gray-400" size={16} />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{need.client}</span>
          {hasOrderClients && (
            <div className="flex flex-wrap gap-1 mt-1">
              {need.orderClients.slice(0, 2).map((orderClient, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800"
                  title={`Order Client: ${orderClient}`}
                >
                  {orderClient.length > 12 ? `${orderClient.substring(0, 12)}...` : orderClient}
                </span>
              ))}
              {need.orderClients.length > 2 && (
                <span 
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                  title={`${need.orderClients.length - 2} more order clients: ${need.orderClients.slice(2).join(', ')}`}
                >
                  +{need.orderClients.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <tr
      key={need.id}
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onRowClick(need)}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(need); } }}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-900">{need.title}</div>
          {isFreshNeed(need.createdAt) && (
            <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200">
              <Sparkles size={10} className="mr-1" />
              NEW
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500 truncate max-w-xs">
          {need.description}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {renderClientCell()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Badge variant={need.priority === 'P1' ? 'danger' : need.priority === 'P2' ? 'warning' : 'info'}>
          {need.priority}
        </Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Badge variant={getStatusColor(need.status)}>
          {getStatusLabel(need.status)}
        </Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <User className="mr-2 text-gray-400" size={16} />
          <span className="text-sm text-gray-900">{getSalesNames(need.consultants)}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {renderCandidatesCell()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-full bg-gray-200 rounded-full h-2 mr-2 min-w-[100px]">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progressPct}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600 min-w-[3rem]">{progressPct}%</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {need.jd_analysis ? (
          <div className="flex items-center space-x-2">
            <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
              <Brain className="w-3 h-3" />
              Analyzed
            </Badge>
            {need.jd_analysis.required_skills && (
              <span className="text-xs text-gray-500">
                {need.jd_analysis.required_skills.length} skills
              </span>
            )}
          </div>
        ) : need.jd_analysis_status === 'PENDING' ? (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Analyzing...
          </Badge>
        ) : (
          <span className="text-gray-400">No analysis</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={(e) => onEditClick(need, e)}
          className="text-indigo-600 hover:text-indigo-900"
        >
          <Edit size={16} />
        </button>
      </td>
    </tr>
  );
};

// ✅ ADD: Define statuses to exclude from main needs list
const EXCLUDED_STATUSES = ['SALE', 'CANCELED', 'DELETED', 'sold', 'canceled', 'deleted'];

const NeedsList = () => {
  const { consultants, columns, refreshNeeds, initialized, error } = useSofteamboardStore();
  // Data comes from the global store – always fresh, no extra HTTP
  const needs = useActiveNeeds();
  const loading = !initialized && !error;
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Candidates data state - centralized at parent level
  const [candidatesData, setCandidatesData] = useState({});
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const candidatesCache = useRef(new Map());
  const abortController = useRef(null);

  // Filter and search state
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [selectedPriorities, setSelectedPriorities] = useState(
    searchParams.get('priorities')?.split(',').filter(Boolean) || []
  );
  const [selectedClients, setSelectedClients] = useState(
    searchParams.get('clients')?.split(',').filter(Boolean) || []
  );
  const [selectedStatuses, setSelectedStatuses] = useState(
    searchParams.get('statuses')?.split(',').filter(Boolean) || []
  );
  const [selectedConsultant, setSelectedConsultant] = useState(
    searchParams.get('consultant') || ''
  );

  // Sort state
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'title');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [editModal, setEditModal] = useState({
    isOpen: false,
    need: null
  });
  const [toast, setToast] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // ProcessTracker state
  const [processTracker, setProcessTracker] = useState({
    isOpen: false,
    needId: null
  });

  // Debounced search
  const debouncedSearch = useDebounce(searchValue, 300);

  // Update URL params when filters change
  const updateUrlParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== '' && (!Array.isArray(value) || value.length > 0)) {
        if (Array.isArray(value)) {
          newParams.set(key, value.join(','));
        } else {
          newParams.set(key, value.toString());
        }
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Optimized candidates fetching - batch requests with caching and deduplication
  const fetchCandidatesForNeeds = useCallback(async (needsList) => {
    if (!needsList || needsList.length === 0) return;

    // Abort previous requests
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    setCandidatesLoading(true);
    setCandidatesError(null);

    try {
      // ✅ FIX: Filter out needs with invalid IDs and only fetch for needs that exist
      const validNeeds = needsList.filter(need => {
        // Check if need has a valid ID (not null, undefined, or empty)
        if (!need || !need.id || typeof need.id !== 'string' || need.id.trim() === '') {
          console.warn('⚠️ Skipping need with invalid ID:', need);
          return false;
        }
        
        // Check if ID looks like a valid UUID or database ID (not truncated)
        if (need.id.includes('…') || need.id.length < 8) {
          console.warn('⚠️ Skipping need with truncated/invalid ID:', need.id);
          return false;
        }
        
        return true;
      });

      // Filter out needs that are already cached and still valid
      const needsToFetch = validNeeds.filter(need => !candidatesCache.current.has(need.id));
      
      if (needsToFetch.length === 0) {
        // All data is cached, just update state
        const cachedData = {};
        validNeeds.forEach(need => {
          if (candidatesCache.current.has(need.id)) {
            cachedData[need.id] = candidatesCache.current.get(need.id);
          }
        });
        setCandidatesData(cachedData);
        setCandidatesLoading(false);
        return;
      }

      // Fetch candidates for needs that aren't cached
      // Use Promise.allSettled to handle individual failures gracefully
      const candidatePromises = needsToFetch.map(async (need) => {
        try {
          const candidates = await candidatesApi.listNeedCandidates(need.id);
          return { needId: need.id, candidates, error: null };
        } catch (error) {
          console.error(`Error fetching candidates for need ${need.id}:`, error);
          // ✅ FIX: Don't treat 404 as an error - just return empty candidates
          if (error.message && error.message.includes('Need not found')) {
            console.info(`Need ${need.id} not found, returning empty candidates`);
            return { needId: need.id, candidates: [], error: null };
          }
          return { needId: need.id, candidates: [], error: error.message };
        }
      });

      const results = await Promise.allSettled(candidatePromises);
      
      // Process results and update cache
      setCandidatesData(prevData => {
        const newCandidatesData = { ...prevData };
        let hasErrors = false;

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const { needId, candidates, error } = result.value;
            if (!error) {
              candidatesCache.current.set(needId, candidates);
              newCandidatesData[needId] = candidates;
            } else {
              hasErrors = true;
              newCandidatesData[needId] = [];
            }
          } else {
            hasErrors = true;
            const needId = needsToFetch[index]?.id;
            if (needId) {
              newCandidatesData[needId] = [];
            }
          }
        });

        // Add cached data for needs that were already cached
        validNeeds.forEach(need => {
          if (candidatesCache.current.has(need.id) && !newCandidatesData[need.id]) {
            newCandidatesData[need.id] = candidatesCache.current.get(need.id);
          }
        });

        if (hasErrors) {
          setCandidatesError('Some candidates could not be loaded');
        }

        return newCandidatesData;
      });

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching candidates:', error);
        setCandidatesError(error.message);
      }
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  // Apply ALL filtering client-side
  const filteredNeeds = useMemo(() => {
    let filtered = needs;
    
    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(need => 
        need.title.toLowerCase().includes(searchLower) ||
        need.client.toLowerCase().includes(searchLower) ||
        (need.description && need.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Priority filtering
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(need => selectedPriorities.includes(need.priority));
    }
    
    // Client filtering
    if (selectedClients.length > 0) {
      filtered = filtered.filter(need => selectedClients.includes(need.client));
    }
    
    // Status filtering
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(need => {
        const status = need.status || need.column;
        return selectedStatuses.includes(status);
      });
    }
    
    // Consultant filtering
    if (selectedConsultant) {
      filtered = filtered.filter(need => {
        const needConsultants = need.consultants || [];
        return needConsultants.includes(selectedConsultant);
      });
    }
    
    return filtered;
  }, [needs, debouncedSearch, selectedPriorities, selectedClients, selectedStatuses, selectedConsultant]);

  // Sort filtered needs client-side
  const sortedNeeds = useMemo(() => {
    const needsToSort = filteredNeeds;
    if (!sortBy) return needsToSort;

    return [...needsToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'progress':
        case 'progressPct':
          aValue = a.progressPct || 0;
          bValue = b.progressPct || 0;
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'client':
          aValue = a.client.toLowerCase();
          bValue = b.client.toLowerCase();
          break;
        case 'priority':
          // P1 = 1, P2 = 2, P3 = 3 for sorting
          aValue = Number.parseInt(a.priority.substring(1));
          bValue = Number.parseInt(b.priority.substring(1));
          break;
        case 'column':
          // Sort by column index
          aValue = columns.findIndex(col => col.id === a.column);
          bValue = columns.findIndex(col => col.id === b.column);
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [filteredNeeds, sortBy, sortOrder, columns]);

  /* ------------------------------------------------------------------
   * On first mount, trigger a single refresh if the cache is empty.
   * Subsequent updates arrive through WebSocket + Provider.
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!needs.length) refreshNeeds();
  }, [needs.length, refreshNeeds]);

  // ✅ ADD: Optional background refresh
  useEffect(() => {
    refreshNeeds().catch(console.error);
  }, []); // Runs once on mount

  // Update URL when search changes
  useEffect(() => {
    updateUrlParams({ search: debouncedSearch });
  }, [debouncedSearch, updateUrlParams]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  // ✅ SIMPLIFIED: Handle filter changes - just update state, no API calls
  const handlePriorityFilter = (priority) => {
    const newPriorities = selectedPriorities.includes(priority)
      ? selectedPriorities.filter(p => p !== priority)
      : [...selectedPriorities, priority];
    setSelectedPriorities(newPriorities);
    updateUrlParams({ priorities: newPriorities.length > 0 ? newPriorities.join(',') : undefined });
  };

  const handleClientFilter = (client) => {
    const newClients = selectedClients.includes(client)
      ? selectedClients.filter(c => c !== client)
      : [...selectedClients, client];
    setSelectedClients(newClients);
    updateUrlParams({ clients: newClients.length > 0 ? newClients.join(',') : undefined });
  };

  const handleStatusFilter = (status) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    setSelectedStatuses(newStatuses);
    updateUrlParams({ statuses: newStatuses.length > 0 ? newStatuses.join(',') : undefined });
  };

  const handleConsultantFilter = (consultantId) => {
    setSelectedConsultant(consultantId);
    updateUrlParams({ consultant: consultantId || undefined });
  };

  const handleSort = (field) => {
    let newSortOrder = 'asc';
    if (sortBy === field && sortOrder === 'asc') {
      newSortOrder = 'desc';
    }
    
    setSortBy(field);
    setSortOrder(newSortOrder);
    setSearchParams({ sortBy: field, sortOrder: newSortOrder });
  };

  const clearAllFilters = () => {
    setSearchValue('');
    setSelectedPriorities([]);
    setSelectedClients([]);
    setSelectedStatuses([]);
    setSelectedConsultant('');
    setSortBy('title');
    setSortOrder('asc');
    setSearchParams({});
  };

  // ✅ ADDED: Handle row click to open ProcessTracker
  const handleRowClick = (need) => {
    setProcessTracker({
      isOpen: true,
      needId: need.id
    });
  };

  // ✅ ADDED: Handle ProcessTracker close
  const handleProcessTrackerClose = () => {
    setProcessTracker({
      isOpen: false,
      needId: null
    });
  };

  // ✅ ADDED: Handle need update from ProcessTracker
  const handleNeedUpdate = () => {
    // Refresh the needs list when a need is updated in ProcessTracker
    refreshNeeds();
  };

  const handleEditClick = (need, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditModal({ isOpen: true, need });
  };

  const handleEditModalClose = () => {
    setEditModal({ isOpen: false, need: null });
  };

  const handleEditSave = () => {
    refreshNeeds();
    setToast({ message: 'Need updated successfully', type: 'success' });
    handleEditModalClose();
  };

  const getStatusColor = (column) => {
    // column ici sera need.status
    const col = columns.find(c => c.id === column);
    return col?.color || 'gray';
  };

  const getStatusLabel = (column) => {
    // column ici sera need.status
    const col = columns.find(c => c.id === column);
    return col?.title || column;
  };

  const getSalesNames = (consultantIds) => {
    if (!consultantIds || consultantIds.length === 0) return '–';
    return consultantIds
      .map(id => consultants.find(c => c.id === id)?.name || 'Unknown')
      .join(', ');
  };

  // ✅ CHANGE: Update unique clients to only include active needs
  const uniqueClients = [...new Set(
    needs
      .filter(need => {
        const status = need.status || need.column;
        return !EXCLUDED_STATUSES.includes(status);
      })
      .map(need => need.client)
  )].sort((a, b) => String(a).localeCompare(String(b)));
  const uniquePriorities = ['P1', 'P2', 'P3'];

  const handleCreateSuccess = async (newNeed, options = {}) => {
    setIsCreateModalOpen(false);
    setToast({
      message: 'Need created successfully',
      type: 'success'
    });
    if (options.openProcessTracker && newNeed.id) {
      try {
        await refreshNeeds();
      } catch (error) {
        console.error('Failed to refresh needs before opening process tracker:', error);
      }
      setProcessTracker({ isOpen: true, needId: newNeed.id });
      return;
    }
    // Refresh to get the new need
    refreshNeeds();
  };

  // ✅ ADD: Handle CSV export
  const handleExportCSV = async () => {
    try {
      setToast({ message: 'Generating CSV export...', type: 'info' });
      
      // Build export parameters based on current filters
      const exportParams = {};
      
      if (final_consultant_id) exportParams.consultant_id = final_consultant_id;
      if (selectedClients.length > 0) exportParams.client = selectedClients[0]; // API supports single client
      if (selectedPriorities.length > 0) exportParams.priority = selectedPriorities[0]; // API supports single priority
      if (debouncedSearch) exportParams.search = debouncedSearch;
      if (selectedConsultant) exportParams.consultantId = selectedConsultant;
      
      await needsApi.exportNeedsCSV(exportParams);
      
      setToast({ message: 'CSV export downloaded successfully', type: 'success' });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setToast({ message: 'Error exporting CSV: ' + error.message, type: 'error' });
    }
  };

  // Use the same logic as the needs endpoint for consultant_id
  const final_consultant_id = selectedConsultant || searchParams.get('consultantId') || searchParams.get('consultant_id');

  if (loading && needs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin mr-2" size={24} />
        <span>Loading needs...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showFilterButton={true}
      />

      <div className="p-6 space-y-6">
        {/* Search and Filters */}
        <div className="space-y-4">
          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Filters</h3>
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Priority Filter */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Priority</span>
                  <div className="space-y-2">
                    {uniquePriorities.map(priority => (
                      <label key={priority} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedPriorities.includes(priority)}
                          onChange={() => handlePriorityFilter(priority)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{priority}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Client Filter */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Client</span>
                  <div className="space-y-3 max-h-32 overflow-y-scroll p-1 custom-scrollbar">
                    {uniqueClients.map(client => (
                      <label key={client} className="flex items-center py-1">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client)}
                          onChange={() => handleClientFilter(client)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="ml-3 text-sm text-gray-700">{client}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Status</span>
                  <div className="space-y-2">
                    {columns.map(column => (
                      <label key={column.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(column.id)}
                          onChange={() => handleStatusFilter(column.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{column.title}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Consultant Filter */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Sales Consultant</span>
                  <select
                    value={selectedConsultant}
                    onChange={(e) => handleConsultantFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All consultants</option>
                    {consultants.map(consultant => (
                      <option key={consultant.id} value={consultant.id}>
                        {consultant.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading needs</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Candidates Error Display */}
        {candidatesError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                <div className="mt-2 text-sm text-yellow-700">{candidatesError}</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {sortedNeeds.length} of {needs.length} active needs
            {candidatesLoading && (
              <span className="ml-2 text-blue-600">
                <Loader className="inline animate-spin mr-1" size={12} />
                Loading candidates...
              </span>
            )}
          </div>
          
          {/* ✅ ADD: Export button */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Export filtered needs as CSV"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center">
                      Title
                      {sortBy === 'title' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                    onClick={() => handleSort('client')}
                  >
                    <div className="flex items-center">
                      Client
                      {sortBy === 'client' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center">
                      Priority
                      {sortBy === 'priority' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                    onClick={() => handleSort('column')}
                  >
                    <div className="flex items-center">
                      Status
                      {sortBy === 'column' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Candidates
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                    onClick={() => handleSort('progressPct')}
                  >
                    <div className="flex items-center">
                      Progress
                      {sortBy === 'progressPct' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    JD Analysis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedNeeds.map((need) => (
                  <NeedRow
                    key={need.id}
                    need={need}
                    consultants={consultants}
                    columns={columns}
                    onEditClick={handleEditClick}
                    onRowClick={handleRowClick}
                    getStatusColor={getStatusColor}
                    getStatusLabel={getStatusLabel}
                    getSalesNames={getSalesNames}
                    candidatesData={candidatesData}
                    candidatesLoading={candidatesLoading}
                    candidatesError={candidatesError}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {sortedNeeds.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-500">No needs found matching your criteria.</div>
            </div>
          )}
        </div>

        {/* Modals */}
        <EditNeedModal
          need={editModal.need}
          open={editModal.isOpen}
          onClose={handleEditModalClose}
          onSuccess={handleEditSave}
        />

        {/* NEW: Need Create Modal */}
        <NeedCreateModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />

        {/* ✅ ADDED: ProcessTracker Modal */}
        <ProcessTracker
          needId={processTracker.needId}
          isOpen={processTracker.isOpen}
          onClose={handleProcessTrackerClose}
          onNeedUpdate={handleNeedUpdate}
        />

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default NeedsList;
